import { Injectable } from '@angular/core';
import { Observable, Subscriber, Subscription, of, throwError } from 'rxjs';
import { AreaItemPack } from '../models/area-item-pack';
import { AreaItem } from '../models/area-item';
import { Router } from '@angular/router';
import { BookingSlot } from '../models/booking-slot';
import { BookingStepEnum } from '../models/booking-step.enum';
import { OrderStateService } from '../../order/services/order-state.service';
import { Order } from '../../order/models/order';
import { LoginStateService } from '../../login/services/login-state.service';


import { LoggerService } from '../../../services/logger/logger.service';
import { PartnerStateService } from '../../partner/services/partner-state.service';
import { BookingOrderService } from './booking-order.service';
import { BreadcrumbsService } from '../../general/services/breadcrumbs.service';
import { InactiveTimerService } from '../../../services/inactive-timer.service';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { BookingSlotStateService } from './booking-slot-state.service';
import { BookingBreadcrumbsService } from './booking-breadcrumbs.service';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { PartnerCodeStateService } from '../../partner-code/services/partner-code-state.service';
import { MatDialog } from '@angular/material/dialog';
import { RemoveOrderQuestionPopupComponent } from '../components/remove-order-question-popup/remove-order-question-popup.component';
import { BookingSlotSelectPolicyEnum } from './booking-slot-select-policy.enum';
import { BaseStepService } from '../../general/services/base-step.services';

@Injectable()
export class BookingStepService extends BaseStepService<BookingStepEnum>{
  private _isEvents: boolean;
  private _initializeOrderServiceSubscription: Subscription;

  constructor(
    protected logger: LoggerService,
    protected inactiveTimerService: InactiveTimerService,
    protected shopSettingsService: ShopSettingsService,
    protected partnerStateService: PartnerStateService,
    protected partnerCodeStateService: PartnerCodeStateService,
    private router: Router,
    private orderStateService: OrderStateService,
    private loginStateService: LoginStateService,
    private bookingOrderService: BookingOrderService,
    private bookingSlotStateService: BookingSlotStateService,
    private breadcrumbsService: BreadcrumbsService,
    private bookingBreadcrumbsService: BookingBreadcrumbsService,
    private dialog: MatDialog,
  ) {
    super(
      logger,
      inactiveTimerService,
      shopSettingsService,
      partnerStateService,
      partnerCodeStateService
    );
    this._steps = [
      BookingStepEnum.ShowProductConfigurationPage,
      BookingStepEnum.SelectBookingExtraProducts,
      BookingStepEnum.Authentication,
      BookingStepEnum.Payment
    ];
  }

  protected get serviceName(): string {
    return BookingStepService.name;
  }

  protected get initStep(): BookingStepEnum {
    return BookingStepEnum.Init;
  }

  protected get resetStep(): BookingStepEnum {
    return BookingStepEnum.Init;
  }

  get isBookingSlotsAvailable(): boolean {
    switch (this.step) {
      case BookingStepEnum.Init:
        return false;
    }
    return true;
  }

  get isBookingEventProductsAvailable(): boolean {
    switch (this.step) {
      case BookingStepEnum.Init:
      case BookingStepEnum.SelectBookingSlots:
        return false;
    }

    return this.bookingOrderService.isEventOrder;
  }

  get isBookingEventSlotProductsAvailable(): boolean {
    switch (this.step) {
      case BookingStepEnum.Init:
        return false;
    }

    return this.bookingOrderService.isEventSlotOrder;
  }

  get isBookingSingleProductsAvailable(): boolean {
    switch (this.step) {
      case BookingStepEnum.Init:
      case BookingStepEnum.SelectBookingSlots:
        return false;
    }

    return this.bookingOrderService.isSingleOrder;
  }

  get isBookingSingleProductConfigurationAvailable(): boolean {
    switch (this.step) {
      case BookingStepEnum.Init:
      case BookingStepEnum.SelectBookingSlots:
      case BookingStepEnum.SelectBookingEventProducts:
      case BookingStepEnum.SelectBookingExtraProducts:
        return false;
    }

    return true;
  }

  get isBookingAdvancedProductsAvailable(): boolean {
    switch (this.step) {
      case BookingStepEnum.Init:
      case BookingStepEnum.SelectBookingSlots:
      case BookingStepEnum.SelectBookingSingleProducts:
      case BookingStepEnum.SelectBookingEventProducts:
        return false;
    }

    return this.bookingOrderService.isAdditionalProductsExist;
  }

  get isBookingOrderAvailable(): boolean {
    switch (this.step) {
      case BookingStepEnum.Init:
      case BookingStepEnum.SelectBookingSlots:
      case BookingStepEnum.SelectBookingSingleProducts:
      case BookingStepEnum.SelectBookingEventProducts:
      case BookingStepEnum.SelectBookingExtraProducts:
        return false;
    }

    return true;
  }

  get isBookingRegistrationAvailable(): boolean {
    switch (this.step) {
      case BookingStepEnum.Init:
      case BookingStepEnum.SelectBookingSlots:
      case BookingStepEnum.SelectBookingSingleProducts:
      case BookingStepEnum.SelectBookingEventProducts:
      case BookingStepEnum.SelectBookingExtraProducts:
      case BookingStepEnum.ShowOrder:
        return false;
    }

    return true;
  }

  get isBookingPaymentAvailable(): boolean {

    if (!this.isBookingRegistrationAvailable) {
      return false;
    }

    return true;
  }

  get isLoggedIn(): boolean {
    return this.loginStateService.isLoggedIn;
  }

  private _showBookingSlotsComponent(
    areaItemPack: AreaItemPack,
    selectedAreaItem: AreaItem,
    isEvent = false,
  ): void {

    this.bookingOrderService.initializeOrderService(areaItemPack, selectedAreaItem)
      .pipe(
        switchMap(initializeResult => {
          if (areaItemPack && areaItemPack.skipBookingSlotPage) {
            return this.bookingSlotStateService.initialize(BookingSlotSelectPolicyEnum.NearestSlot)
              .pipe(
                map(_ => {
                  const timeSlot = this.bookingSlotStateService.selectedSlot;
                  if (!timeSlot) {
                    return initializeResult;
                  }
                  const selectedBookingDate = this.bookingSlotStateService.getSelectedBookingDate(timeSlot);
                  this._showBookingProductsComponent(selectedBookingDate, [timeSlot]);
                  return false;
                })
              );
          } else {
            return of(initializeResult);
          }
        })
      )
      .subscribe(result => {
        if (!result) {
          return;
        }
        if (isEvent) {
          this.breadcrumbsService.setBreadcrumbLevelUrl(0, '/booking/event');
          this.router.navigateByUrl('/booking/event/slots');
        } else {
          this.router.navigateByUrl('/booking/slots');
        }
      });
  }

  private _showBookingEventAreaItemSlotProductsComponent(
    selectedAreaItem: AreaItem,
    selectedBookingDate: Date,
    selectedBookingSlots: BookingSlot[],
  ): void {
    if (!selectedAreaItem || !selectedBookingSlots || selectedBookingSlots.length === 0 || !selectedBookingSlots[0]) {
      this.update(BookingStepEnum.Reset);
      return;
    }

    this.bookingOrderService.initializeEventSlotOrderService(selectedAreaItem, selectedBookingSlots[0]).subscribe(
      result => {
        this.bookingOrderService.setSelectedBookingDateAndSlots(selectedBookingDate, selectedBookingSlots);
        // this.breadcrumbsService.setBreadcrumbLevelUrl(0, '/booking/events/product');
        this.router.navigateByUrl('/booking/events/product');
      }
    );
  }

  private _showBookingComponent(
    selectedBookingDate: Date,
    selectedBookingSlots: BookingSlot[],
    url: string
  ): void {

    this.bookingOrderService.setSelectedBookingDateAndSlots(selectedBookingDate, selectedBookingSlots);

    this.router.navigateByUrl(url);
  }

  private _showNearestBookingSingleProducts(
    areaItemPack: AreaItemPack,
    selectedAreaItem: AreaItem,
  ): void {
    this._unsubscribeInitializeOrderServiceSubscription();
    this._initializeOrderServiceSubscription = this.bookingOrderService.initializeOrderService(areaItemPack, selectedAreaItem)
      .pipe(
        switchMap(_ => {
          return this.bookingSlotStateService.initialize();
        }),
        switchMap(_ => {
          return this.bookingSlotStateService.dateSelect(new Date());
        }),
        tap((done) => {
          const timeSlot = this.bookingSlotStateService.selectedSlot;
          if (!done || !timeSlot) {
            this.router.navigateByUrl('/booking/slots');
          } else {
            this.breadcrumbsService.setBreadcrumbLevelUrl(1, '/booking/slots');
            const selectedBookingDate = this.bookingSlotStateService.getSelectedBookingDate(timeSlot);
            this._showBookingProductsComponent(selectedBookingDate, [timeSlot]);
          }
        })
      )
      .subscribe();
  }

  private _unsubscribeInitializeOrderServiceSubscription(): void {
    if (this._initializeOrderServiceSubscription) {
      this._initializeOrderServiceSubscription.unsubscribe();
      this._initializeOrderServiceSubscription = null;
    }
  }

  private _showBookingProductsComponent(
    selectedBookingDate: Date,
    selectedBookingSlots: BookingSlot[]
  ): void {
    this._showBookingComponent(selectedBookingDate, selectedBookingSlots, '/booking/single/product');
  }

  private _showBookingEventsComponent(
    selectedBookingDate: Date,
    selectedBookingSlots: BookingSlot[],
  ): void {
    if (this.bookingOrderService.isBirthday) {
      this._showBookingComponent(selectedBookingDate, selectedBookingSlots, '/booking/event/product');
    }
    if (this.bookingOrderService.isGroup) {
      this.bookingOrderService.setSelectedBookingDateAndSlots(selectedBookingDate, selectedBookingSlots);
      this.update(BookingStepEnum.ShowOrder);
    }
  }

  private _showRootComponent(): void {
    this.router.navigateByUrl('/booking');
  }

  private _showAdditionalProductsComponent(): void {
    this.router.navigateByUrl('/booking/advanced-product');
  }

  private _showOrderComponent(): void {
    this.router.navigateByUrl('/booking/order');
  }

  private _showAuthenticationComponent(): void {
    this.router.navigateByUrl('/booking/authentication');
  }

  private _showLoginComponent(): void {
    this.router.navigateByUrl('/booking/login');
  }

  private _showRegisterComponent(): void {
    this.router.navigateByUrl('/booking/register');
  }

  private _showPaymentComponent(): void {
    this.router.navigateByUrl('/payment/start');
  }

  private _showGroupEventComponent(): void {
    this.router.navigateByUrl('/booking/group-event');
  }

  private _showGroupEventThankYouComponent(): void {
    this.router.navigateByUrl('/booking/contact-thank-you');
  }

  private _showProductConfigurationPage(): void {
    this.router.navigateByUrl('/booking/single/product/configuration');
  }

  reinitialize(): Observable<boolean> {
    return super.reinitialize().pipe(
      switchMap(_ => this.bookingOrderService.reinitialize()),
      tap(_ => this.logger.info(`${this.serviceName}: bookingOrderService reinitialize done.`)),
      switchMap(_ => this.bookingOrderService.cancelVUActiveOrder()),
      catchError(error => {
        this.logger.info(`${this.serviceName}: bookingOrderService reinitialize failed ${error}.`);
        return throwError(error);
      }),
    );
  }

  protected _internalInitialize(): void {
    super._internalInitialize();
    this._isEvents = false;
    this._breadcrumbsReload();
  }

  private _breadcrumbsReload(): void {
    this.bookingBreadcrumbsService.reload(this.step, this._isEvents);
  }

  initialize(): Observable<boolean> {
    return super.initialize().pipe(
      switchMap(_ => this.bookingOrderService.initialize()),
      tap(_ => this.logger.info(`${this.serviceName}: bookingOrderService initialize done.`)),
      catchError(error => {
        this.logger.info(`${this.serviceName}: bookingOrderService initialize failed ${error}.`);
        return throwError(error);
      }),
    );
  }

  initializeEventsAreaItemSlots(): Observable<boolean> {
    return of(true).pipe(
      tap(_ => {
        this.logger.info(`${this.serviceName}: initializeEventsAreaItemSlots.`);
        this._internalInitialize();
        this._isEvents = true;
      }),
      switchMap(_ => this.bookingOrderService.initializeEventsAreaItemSlots()),
      tap(_ => this.logger.info(`${this.serviceName}: bookingOrderService initializeEventsAreaItemSlots done.`)),
      catchError(error => {
        this.logger.info(`${this.serviceName}: bookingOrderService initializeEventsAreaItemSlots failed ${error}.`);
        return throwError(error);
      }),
    );
  }

  update(
    step: BookingStepEnum = BookingStepEnum.Init,
    areaItemPack: AreaItemPack = null,
    selectedAreaItem: AreaItem = null,
    selectedBookingDate: Date = null,
    selectedBookingSlots: BookingSlot[] = null,
    paymentOrder: Order = null
  ): void {
    super.update(step);
    this._breadcrumbsReload();

    switch (this._step) {
      case BookingStepEnum.SelectBookingPacks:
        this._showBookingSlotsComponent(areaItemPack, selectedAreaItem);
        break;

      case BookingStepEnum.SelectBookingEventsPacks:
        this._showBookingSlotsComponent(areaItemPack, selectedAreaItem, true);
        break;

      case BookingStepEnum.SelectNearestBookingSingleProducts:
        this._showNearestBookingSingleProducts(areaItemPack, selectedAreaItem);
        break;

      case BookingStepEnum.SelectBookingSingleProducts:
        this._showBookingProductsComponent(selectedBookingDate, selectedBookingSlots);
        break;
      case BookingStepEnum.SelectBookingEventProducts:
        this._showBookingEventsComponent(selectedBookingDate, selectedBookingSlots);
        break;

      case BookingStepEnum.SelectBookingEventAreaItemSlots:
        this._showBookingEventAreaItemSlotProductsComponent(selectedAreaItem, selectedBookingDate, selectedBookingSlots);
        break;

      case BookingStepEnum.SelectBookingExtraProducts:
        this._showAdditionalProductsComponent();
        break;

      case BookingStepEnum.ShowOrder:
        this._showOrderComponent();
        break;

      case BookingStepEnum.Authentication:
        this._showAuthenticationComponent();
        break;

      case BookingStepEnum.Login:
        this._showLoginComponent();
        break;

      case BookingStepEnum.Register:
      case BookingStepEnum.RegisterAsGuest:
        this._showRegisterComponent();
        break;

      case BookingStepEnum.Payment:
        this._showPaymentComponent();
        break;

      case BookingStepEnum.GroupEvent:
        this._showGroupEventComponent();
        break;

      case BookingStepEnum.GroupEventThankYou:
        this.inactiveTimerService.clearTimer();
        this._showGroupEventThankYouComponent();
        break;

      case BookingStepEnum.Reset:
        this._showRootComponent();
        break;

      case BookingStepEnum.ShowProductConfigurationPage:
        this._showProductConfigurationPage();
        break;

      case BookingStepEnum.Init:
      default:
        this.initialize().subscribe();
        break;
    }
  }

  forward(): void {
    if (this.nextStep()) {
      return;
    }

    this.update(BookingStepEnum.Reset);
  }

  isAvailableStep(step: BookingStepEnum): boolean {
    if (!step) {
      return false;
    }

    switch (step) {
      case BookingStepEnum.ShowProductConfigurationPage:
        return this.bookingOrderService.isShowProductConfigurationPage();
      case BookingStepEnum.SelectBookingExtraProducts:
        return this.bookingOrderService.isAdditionalProductsExist;
      case BookingStepEnum.Authentication:
        return this.needAuth;
      default:
        return true;
    }
  }

  returnToEventAreaItemSlots(): void {
    this.update(
      BookingStepEnum.SelectBookingEventAreaItemSlots,
      null,
      this.bookingOrderService.selectedAreaItem,
      this.bookingOrderService.selectedBookingDate,
      [this.bookingOrderService.eventAreaItemBookingSlot]
    );
  }


  back(): void {
    if (this.prevStep()) {
      return;
    }

    switch (this._step) {
      case BookingStepEnum.SelectBookingEventAreaItemSlots:
        this.update(BookingStepEnum.Init);
        this.router.navigateByUrl('/booking/events');
        break;
      case BookingStepEnum.SelectBookingEventProducts:
        this.update(BookingStepEnum.SelectBookingPacks, this.bookingOrderService.areaAreaItemPack);
        break;
      case BookingStepEnum.SelectNearestBookingSingleProducts:
        this.update(BookingStepEnum.Reset);
        break;
      case BookingStepEnum.SelectBookingSingleProducts:
        if (this.bookingOrderService.areaAreaItemPack
          && this.bookingOrderService.areaAreaItemPack.purchaseNearestTicket
          || this.skipBookingSlotPage) {
          this.update(BookingStepEnum.Reset);
          return;
        }
        this.update(BookingStepEnum.SelectBookingPacks, this.bookingOrderService.areaAreaItemPack);
        break;
      default:
        if (this.bookingOrderService.isEventSlotOrder) {
          this.returnToEventAreaItemSlots();
          return;
        }
        let updateStep = BookingStepEnum.SelectBookingSingleProducts;

        if (this.bookingOrderService.isEventTypeOrder) {
          if (this.bookingOrderService.isBirthday) {
            updateStep = BookingStepEnum.SelectBookingEventProducts;
          }
          if (this.bookingOrderService.isGroup) {
            updateStep = BookingStepEnum.SelectBookingEventsPacks;
          }
        }

        this.update(
          updateStep, this.bookingOrderService.areaAreaItemPack, this.bookingOrderService.selectedAreaItem
        );
        break;
    }
  }

  get skipBookingSlotPage(): boolean {
    return this.bookingOrderService
      && this.bookingOrderService.areaAreaItemPack
      && this.bookingOrderService.areaAreaItemPack.skipBookingSlotPage;
  }

  canResetOrderQuestionPopup(): Observable<any> {
    return of(true).pipe(
      switchMap(_ => {
        if (!this.orderStateService.order
          || !this.orderStateService.order.orderLines
          || this.orderStateService.order.orderLines.length === 0
          || this.shopSettingsService.shopSettings.multiOrderMode) {
          return of(_);
        }

        const dialogRef = this.dialog.open(RemoveOrderQuestionPopupComponent);
        return dialogRef.afterClosed().pipe(
          map(() => dialogRef.componentInstance.result)
        );
      })
    );
  }

  initFromLoadOrder(step: BookingStepEnum): Observable<any> {
    return of(true).pipe(
      tap(_ => {

        this.orderStateService.orderLoadFromLocalStorage();
        this._step = step;
        this._stepIndex = this._steps.indexOf(step);
      }),
      switchMap(_ => {
        return this.bookingOrderService.reinitializeFromOrder();
      }),
      switchMap(_ => {
        return this.bookingSlotStateService.dateSelect(this.orderStateService.order.bookingDate);
      }),
      tap(_ => {
        const selectedDate = this.bookingSlotStateService.getSelectedBookingDate(this.bookingSlotStateService.selectedSlot);
        this.bookingOrderService.setSelectedBookingDateAndSlots(
          selectedDate, [this.bookingSlotStateService.selectedSlot], true);
      }),
    );
  }
}
